'use client';
import { fetchBondGraphData } from '@/fetchSSR/bondDetail/getBondsDetail';
import dynamic from 'next/dynamic';
import React, { memo, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

// Dynamically import HighchartsReact with SSR disabled
const HighchartsReact = dynamic(() => import('highcharts-react-official'), {
    ssr: false,
    loading: () => <GraphSkeleton />,
});

interface IncrementalPriceGraphProps {
    getBondPrice?: (body: any) => void;
    bondPriceChartsData?: any;
    isbondPriceChartsDataLoader?: boolean;
    highchartsProps?: any;
    graphDetail?: {
        name?: string;
        isin?: string;
    };
    prevBond?: { isin?: string };
    nextBond?: { isin?: string };
    handlePreviousBondClick?: () => void;
    handleNextBondClick?: () => void;
    GraphData?: number[][];
    bondID?: string;
}

const IncrementalPriceGraph: React.FC<IncrementalPriceGraphProps> = ({
    graphDetail,
    prevBond,
    nextBond,
    handlePreviousBondClick,
    handleNextBondClick,
    GraphData = [],
    bondID,
}) => {
    const [isClient, setIsClient] = useState(false);
    const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);
    const [HighchartsInstance, setHighchartsInstance] = useState<any>(null);

    const getGraphData = async () => {
        const graphData = await fetchBondGraphData(bondID);

        const Highcharts = require('highcharts/highstock');
        setHighchartsInstance(Highcharts);

        // Create options with Highcharts now that it's available
        setChartOptions({
            chart: {
                type: 'area',
                height: 460,
            },
            title: {
                text: graphDetail?.name ?? 'Price Chart',
                align: 'left',
            },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                title: {
                    text: 'Price',
                },
            },
            rangeSelector: {
                buttons: [
                    {
                        type: 'day',
                        count: 5,
                        text: '5d', // custom button
                    },
                    {
                        type: 'month',
                        count: 1,
                        text: '1m',
                    },
                    {
                        type: 'month',
                        count: 3,
                        text: '3m',
                    },
                    {
                        type: 'month',
                        count: 6,
                        text: '6m',
                    },
                    {
                        type: 'ytd',
                        text: 'YTD',
                    },
                    {
                        type: 'year',
                        count: 1,
                        text: '1y',
                    },
                    {
                        type: 'all',
                        text: 'All',
                    },
                ],
            },
            plotOptions: {
                area: {
                    fillColor: {
                        linearGradient: {
                            x1: 0,
                            y1: 0,
                            x2: 0,
                            y2: 1,
                        },
                        stops: [
                            [0, Highcharts.getOptions().colors?.[0] ?? '#7cb5ec'],
                            [
                                1,
                                Highcharts.color(Highcharts.getOptions().colors?.[0] ?? '#7cb5ec')
                                    .setOpacity(0)
                                    .get('rgba') as string,
                            ],
                        ],
                    },
                    marker: {
                        radius: 2,
                    },
                    lineWidth: 1,
                    states: {
                        hover: {
                            lineWidth: 1,
                        },
                    },
                    threshold: null,
                },
            },
            series: [
                {
                    type: 'area',
                    name: 'Price',
                    data: graphData?.length ? graphData : [],
                    animation: false,
                },
            ],
            credits: {
                enabled: false,
            },
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            getGraphData();
            setIsClient(true);
        }
    }, [graphDetail?.name, bondID]);

    return (
        <div>
            {handlePreviousBondClick && (
                <div className="d-flex align-items-center gap-1">
                    <span className="d-flex flex-column align-items-center gap-0 ms-1">
                        <span className="cursor-pointer m-0 p-0" onClick={() => prevBond?.isin && handlePreviousBondClick()}>
                            ▾
                        </span>
                        <span
                            className="cursor-pointer m-0 p-0"
                            onClick={() => nextBond?.isin && handleNextBondClick && handleNextBondClick()}
                        >
                            ▿
                        </span>
                    </span>
                    <span
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1em',
                            color: 'rgb(51, 51, 51)',
                        }}
                    >
                        {graphDetail?.name ?? ''}
                    </span>
                </div>
            )}
            {!isClient || !chartOptions || !HighchartsInstance ? (
                <GraphSkeleton />
            ) : !!chartOptions?.series?.[0]?.data?.length ? (
                <HighchartsReact highcharts={HighchartsInstance} options={chartOptions} constructorType={'stockChart'} />
            ) : (
                <div
                    className="text-center p-3 col col-lg-6 rounded mx-auto d-flex align-items-start justify-content-center gap-1 mt-4"
                    style={{ color: '#b73c10', backgroundColor: '#fef1ec' }}
                >
                    <p className="fw-semibold">
                        ⚠️ Oops! This bond doesn’t have historical data yet. We’ll draw the lines as soon as the numbers arrive!
                    </p>
                </div>
            )}
        </div>
    );
};

export const GraphSkeleton = () => (
    <div style={{ padding: '10px', width: '100%', maxWidth: '1100px' }}>
        {/* Header / Controls */}
        <div style={{ marginBottom: '16px' }}>
            <Skeleton height={20} width={90} style={{ marginRight: '10px' }} inline />
        </div>
        <Skeleton height={20} width={250} className="mb-3" />

        {/* Chart area */}
        <Skeleton height={200} className="mt-4 pt-4" width="100%" borderRadius={4} highlightColor="#a6cdf1ff" baseColor="#deefffff" />

        {/* Volume bars area */}
        <div style={{ marginTop: '16px' }}>
            <Skeleton height={60} width="100%" borderRadius={4} highlightColor="#a6cdf1ff" baseColor="#c6d6e5ff" />
        </div>
    </div>
);

export default memo(IncrementalPriceGraph);
